import * as React from 'react';

export const WEBFLOW_SEARCH_HOLD_KEY = 'global-search';
export const WEBFLOW_NOTIFICATIONS_HOLD_KEY = 'notifications';

type WebflowUtilitiesLifecycleValue = {
  /** Keep the utilities portal mounted while a TW overlay (search, menu) is open. */
  holdOpen: (key?: string) => void;
  releaseHold: (key?: string) => void;
  isHeld: boolean;
  /** True while search modal hold is active (icons should conceal). */
  isSearchHeld: boolean;
};

const WebflowUtilitiesLifecycleContext =
  React.createContext<WebflowUtilitiesLifecycleValue | null>(null);

const DEFAULT_HOLD_KEY = 'default';

/**
 * Provides hold/release so Chakra overlays from the TW utilities cluster
 * (e.g. GlobalSearch) survive when the Webflow mobile menu closes the slot.
 * Only mounted under Webflow-gated portals.
 */
export const WebflowUtilitiesLifecycleProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [holdKeys, setHoldKeys] = React.useState<string[]>([]);

  const holdOpen = React.useCallback((key: string = DEFAULT_HOLD_KEY) => {
    setHoldKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const releaseHold = React.useCallback((key: string = DEFAULT_HOLD_KEY) => {
    setHoldKeys((prev) => prev.filter((k) => k !== key));
  }, []);

  const isHeld = holdKeys.length > 0;
  const isSearchHeld = holdKeys.includes(WEBFLOW_SEARCH_HOLD_KEY);

  const value = React.useMemo(
    () => ({ holdOpen, releaseHold, isHeld, isSearchHeld }),
    [holdOpen, releaseHold, isHeld, isSearchHeld]
  );

  return (
    <WebflowUtilitiesLifecycleContext.Provider value={value}>
      {children}
    </WebflowUtilitiesLifecycleContext.Provider>
  );
};

export const useWebflowUtilitiesLifecycle =
  (): WebflowUtilitiesLifecycleValue | null =>
    React.useContext(WebflowUtilitiesLifecycleContext);
